<script lang="ts" setup>
import {
  FrontiersImage,
  formatCountSuffix,
  CustomLink,
  LinkIcon,
  LinkIconLocation,
  LinkColor,
  type ISanitizeOptions
} from "@frontiers-vue3/public-pages";
import type { IImpactBoxItem } from "~/components/templates/v2/ImpactBox.vue";
import type { ITopicEditorProps } from "~/components/templates/v2/TopicEditor/TopicEditor.models";
import ListTopicEditors from "~/components/templates/v2/ListTopicEditors.vue";
import ImpactBox from "~/components/templates/v2/ImpactBox.vue";
import {
  isoToDateConverter,
  replaceDynamicVars,
  numberCommaSeparator
} from "~/helpers";
import { sanitizeResearchTopicTitle } from "~/helpers/sanitizeResearchTopicTitle";

const store = useMain();
const { currentResearchTopic, isCurrentResearchTopicOpen } = store;
const config = useRuntimeConfig();
const terminology = useTerminologyStore();
const { isImpactMetricsVisible, minimumRtDownloadsCount } =
  useEnvironmentConfigStore().getConfig();
const sanitize = inject("sanitize") as (
  html: string,
  options?: ISanitizeOptions
) => string;

const { downloads = 0, views = 0 } = currentResearchTopic.impactV5 || {};
const impactItems: IImpactBoxItem[] = [];

if (isImpactMetricsVisible && downloads > minimumRtDownloadsCount) {
  const { value, suffix } = formatCountSuffix(downloads);
  impactItems.push({
    value: `${numberCommaSeparator(value || 0)}${suffix || ""}`,
    label: "Total downloads"
  });
}

if (isImpactMetricsVisible && views > minimumRtDownloadsCount) {
  const { value, suffix } = formatCountSuffix(views);
  impactItems.push({
    value: `${numberCommaSeparator(value || 0)}${suffix || ""}`,
    label: "Total views and downloads"
  });
}

const topicEditors: ITopicEditorProps[] = (
  currentResearchTopic.topicEditors || []
).map((author) => {
  const aff = author.affiliations?.[0];
  return {
    loopUserId: author.userId,
    fullName: author.fullName,
    organizationName: aff?.organization || "",
    organizationLocation: [aff?.city, aff?.country].filter(Boolean).join(", ")
  };
});

const titleHtml = sanitizeResearchTopicTitle(
  sanitize,
  currentResearchTopic.title
);

const currentDate = new Date(new Date().toISOString());
const deadline = new Date(currentResearchTopic.manuscriptSubmissionDeadline);
const extendedDeadline = new Date(
  currentResearchTopic.manuscriptSubmissionExtendedDeadline
);
const hideManuscriptSubmissionDeadline =
  String(config.public?.hideManuscriptSubmissionDeadline).toLowerCase() ===
  "true";
const showAcceptingArticlesText =
  String(config.public?.showAcceptingArticlesText).toLowerCase() === "true";

const isManuscriptExtensionDeadlineVisible = (() => {
  const minDate = new Date("01/01/0001");
  let hideManuscriptSubmissionFromDate = "";
  if (config.public.hideManuscriptSubmissionFromDate) {
    const [sDate, sMonth, sYear] =
      config.public.hideManuscriptSubmissionFromDate.split("/");
    hideManuscriptSubmissionFromDate = `${sMonth}/${sDate}/${sYear}`;
  }
  const createdDate = currentResearchTopic.createdDate
    ? new Date(currentResearchTopic.createdDate)
    : minDate;

  return (
    (createdDate < new Date(hideManuscriptSubmissionFromDate) &&
      currentDate <= extendedDeadline) ||
    (currentDate > deadline &&
      currentDate <= extendedDeadline &&
      !hideManuscriptSubmissionDeadline)
  );
})();

const showDeadline =
  isCurrentResearchTopicOpen &&
  (currentDate <= deadline || isManuscriptExtensionDeadlineVisible);
const deadlineText = showDeadline
  ? currentDate <= deadline
    ? `Submission deadline: ${isoToDateConverter(deadline, "long")}`
    : `Submission deadline: ${isoToDateConverter(extendedDeadline, "long")}`
  : "";

const submissionMessage = (() => {
  if (!isCurrentResearchTopicOpen || !showAcceptingArticlesText) return "";

  if (currentDate <= deadline) {
    return "This Research Topic is currently accepting articles.";
  }

  if (
    currentDate > extendedDeadline ||
    (currentDate > deadline && hideManuscriptSubmissionDeadline)
  ) {
    return "This Research Topic is currently accepting articles, but is closing soon.";
  }

  return "";
})();

const authorGuidelinesUrl = (() => {
  const link = config.public.researchTopicPageAuthorGuidelinesLink || "";
  const primaryJournalSlug = store.getPrimaryJournal?.slug || "";
  return replaceDynamicVars(link, { primaryJournalSlug });
})();

const authorGuidelinesLink = {
  text: `Check ${terminology.getTerm("author").toLowerCase()} guidelines`,
  url: authorGuidelinesUrl,
  target: "_blank"
};

const showAuthorGuidelinesLink =
  isCurrentResearchTopicOpen && !!authorGuidelinesUrl;

const showSubmissionInfo =
  config.public.isSubmissionDeadlinesVisible &&
  (showDeadline || submissionMessage || isCurrentResearchTopicOpen);
</script>
<template>
  <header class="Layout__header RTHeader">
    <div class="RTHeader__journal">
      <div class="RTHeader__pretitle">
        <p class="RTHeader__label">RESEARCH TOPIC</p>
        <p
          v-if="isCurrentResearchTopicOpen"
          class="RTHeader__chip">
          OPEN
        </p>
      </div>
      <!-- eslint-disable-next-line vue/no-v-html -- Title is sanitized with allowed tags -->
      <h1
        class="RTHeader__title"
        v-html="titleHtml"></h1>
      <ListTopicEditors
        v-if="topicEditors.length"
        :editors="topicEditors" />
    </div>
    <div class="RTHeader__right">
      <div
        v-if="currentResearchTopic.imageUrl"
        class="RTHeader__img">
        <FrontiersImage
          :sources="[currentResearchTopic.imageUrl]"
          :image-alt="`${terminology.getTerm('researchTopic')} image`" />
      </div>
      <ImpactBox :items="impactItems" />
    </div>
    <div
      v-if="showSubmissionInfo"
      class="RTHeader__submission">
      <div class="RTHeader__submission__info">
        <p
          v-if="deadlineText"
          class="RTHeader__submission__deadline">
          {{ deadlineText }}
        </p>
        <p
          v-if="submissionMessage"
          class="RTHeader__submission__message">
          {{ submissionMessage }}
        </p>
      </div>
      <CustomLink
        v-if="showAuthorGuidelinesLink"
        class="RTHeader__submission__link"
        :link="authorGuidelinesLink"
        :color="LinkColor.Grey"
        :icon="LinkIcon.ArrowRight"
        :icon-location="LinkIconLocation.Right" />
    </div>
  </header>
</template>
<style lang="scss">
.RTHeader {
  $root: &;
  display: grid;
  grid-template-columns: 2fr 1fr;
  column-gap: spacing(4);
  row-gap: spacing(2);
  align-items: center;

  @media #{$break-xs} {
    flex-wrap: wrap;
    grid-template-columns: 1fr;
  }

  &__pretitle {
    display: flex;
    gap: spacing(2);
    align-items: end;
    margin-bottom: spacing(1);
  }

  &__label {
    @include f("heading-sm-bold");
    color: $textPrimary;
    margin-bottom: spacing(0.5);
  }

  &__chip {
    @include f("label-md-semibold");
    color: $green80;
    padding: spacing(0.5) spacing(2);
    border-radius: spacing(2);
    background: $green0;
    border: 1px solid $green20;
    vertical-align: top;
  }

  &__right {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: spacing(3);

    @media #{$break-md} {
      gap: spacing(2);
    }

    @media #{$break-xs} {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }
  }

  &__img {
    width: 100%;
    aspect-ratio: 16/9;
    border-radius: spacing(1);
    overflow: hidden;

    .FrontiersImage {
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  }

  &__journal {
    flex: 1;
  }

  &__title {
    @include f("fluid-heading-2xl");
    color: $textPrimary;
    margin-bottom: spacing(2);

    @media #{$break-md} {
      @include f("fluid-heading-xl");
    }

    span {
      @include f("fluid-heading-md");
    }

    i,
    em {
      font-style: italic;
    }
  }

  &__submission {
    grid-column: 1 / -1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: spacing(2);
    border-top: 1px solid $grey40;
    gap: spacing(3);

    @media #{$break-sm} {
      flex-direction: column;
      align-items: flex-start;
    }

    &__deadline {
      @include f("label-md-semibold");
      color: $textPrimary;
    }

    &__message {
      @include f("label-md");
      color: $textTertiary;
    }
  }
}
</style>
